import React from "react";
import { Box, Typography } from "@mui/material";
import { SelectBandItem } from "./useSelectBandReport";

const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

interface SelectBandListProps {
  items: SelectBandItem[];
  labelHeader?: string;
  limit?: number;
  showSecondary?: boolean;
  metricLabel?: string;
}

export const SelectBandList = ({
  items,
  labelHeader = "Band name",
  limit,
  showSecondary = false,
  metricLabel = "Selections",
}: SelectBandListProps) => {
  const displayed = limit ? items.slice(0, limit) : items;
  const maxValue = Math.max(...displayed.map((item) => item.totalSelections), 0);

  if (displayed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          color: "text.secondary",
        }}
      >
        <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
          {labelHeader}
        </Typography>
        <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
          {metricLabel}
        </Typography>
      </Box>
      {displayed.map((item) => {
        const widthPercent =
          maxValue > 0 ? (item.totalSelections / maxValue) * 100 : 0;
        return (
          <Box
            key={item.name}
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              pb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Typography variant="subtitle1" noWrap>
                {item.name}
              </Typography>
              <Typography variant="subtitle2">
                {formatCompactNumber(item.totalSelections)}
              </Typography>
            </Box>
            {item.priorities.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {item.priorities
                  .map((p) => `P${p.priority}: ${formatCompactNumber(p.count)}`)
                  .join(" · ")}
              </Typography>
            )}
            <Box
              sx={{
                height: 4,
                mt: 0.75,
                backgroundColor: "divider",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${widthPercent}%`,
                  backgroundColor: "#2f6fed",
                }}
              />
            </Box>
            {showSecondary && item.clearedCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                Cleared: {formatCompactNumber(item.clearedCount)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
